import { useState, useEffect, useCallback } from 'react'
import type { AxiosResponse } from 'axios'
import type { PaginatedResponse } from '@/types/api'

interface UseAdminTabResult<T> {
  data: T[]
  meta: PaginatedResponse<T>['meta'] | null
  loading: boolean
  page: number
  setPage: (n: number) => void
  refresh: () => void
}

export function useAdminTab<T>(
  fetcher: (page: number) => Promise<AxiosResponse<PaginatedResponse<T>>>,
  active: boolean,
): UseAdminTabResult<T> {
  const [data, setData] = useState<T[]>([])
  const [meta, setMeta] = useState<PaginatedResponse<T>['meta'] | null>(null)
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(1)
  const [fetchCount, setFetchCount] = useState(0)

  useEffect(() => {
    if (!active) return

    setLoading(true)
    fetcher(page)
      .then((res) => {
        setData(res.data.data)
        setMeta(res.data.meta)
      })
      .finally(() => setLoading(false))
  }, [active, page, fetcher, fetchCount])

  const refresh = useCallback(() => {
    setFetchCount((c) => c + 1)
  }, [])

  return { data, meta, loading, page, setPage, refresh }
}
